import type {
  ContractLifecycleEnvelope,
  ContractLifecycleMessage,
  ContractMarkerInput,
} from "./trades";
import { addTradeMarker, applyContractLifecycleEvent } from "./trades";
import { goToLatest } from "./time";
import type { State, TradeDirection, TradeDurationUnit, TradeMarker } from "./types";

export type TradeExecutionDraft = {
  contractType: string;
  duration: number;
  durationUnit: TradeDurationUnit;
  stake: number;
};

export type TradeExecutionPayload = TradeExecutionDraft & {
  direction: TradeDirection;
  placedAt: number;
  accepted: boolean;
  contractId?: string | number;
  entryPrice?: number;
  entryTimeEpoch?: number;
  barrier?: number;
  barrierHigh?: number;
  barrierLow?: number;
  digitTarget?: number;
  digitLow?: number;
  digitHigh?: number;
  expiryTimeEpoch?: number;
};

export type TradeExecutionSubmission = Partial<
  Pick<
    ContractMarkerInput,
    | "contractId"
    | "contractType"
    | "duration"
    | "durationUnit"
    | "entryPrice"
    | "entryTimeEpoch"
    | "barrier"
    | "barrierHigh"
    | "barrierLow"
    | "digitTarget"
    | "digitLow"
    | "digitHigh"
    | "expiryTimeEpoch"
  >
>;

export type TradeExecutionAdapter = {
  getDraft(): TradeExecutionDraft;
  setContractType(contractType: string): void;
  setDuration(duration: number, durationUnit?: TradeDurationUnit): void;
  setDurationSeconds(durationSeconds: number): void;
  setDurationTicks(durationTicks: number): void;
  setStake(stake: number): void;
  applyLifecycleEvent(event: ContractLifecycleMessage | ContractLifecycleEnvelope): TradeMarker | undefined;
  execute(direction: TradeDirection, details?: TradeExecutionSubmission): boolean;
  executeBuy(details?: TradeExecutionSubmission): boolean;
  executeSell(details?: TradeExecutionSubmission): boolean;
};

export type TradeExecutionOptions = {
  state: State;
  redraw: () => void;
  onExecute?: (payload: TradeExecutionPayload) => void;
  defaultContractType?: string;
  defaultDurationSeconds?: number;
  defaultStake?: number;
  autoFollowLatest?: boolean;
};

export function createTradeExecutionAdapter(options: TradeExecutionOptions): TradeExecutionAdapter {
  const {
    state,
    redraw,
    onExecute,
    defaultContractType = "Rise/Fall",
    defaultDurationSeconds = 60,
    defaultStake = 10,
    autoFollowLatest = true,
  } = options;

  const draft: TradeExecutionDraft = {
    contractType: defaultContractType,
    duration: Math.max(1, Math.floor(defaultDurationSeconds)),
    durationUnit: "s",
    stake: Math.max(1, Math.floor(defaultStake)),
  };

  function execute(direction: TradeDirection, details: TradeExecutionSubmission = {}): boolean {
    const contractType = (details.contractType ?? draft.contractType).trim() || defaultContractType;
    const duration = Math.max(1, Math.floor(details.duration ?? draft.duration));
    const durationUnit = details.durationUnit ?? draft.durationUnit;
    const payload: TradeExecutionPayload = {
      ...draft,
      contractType,
      duration,
      durationUnit,
      direction,
      placedAt: Date.now(),
      accepted: false,
      contractId: details.contractId,
      entryPrice: details.entryPrice,
      entryTimeEpoch: details.entryTimeEpoch,
      barrier: details.barrier,
      barrierHigh: details.barrierHigh,
      barrierLow: details.barrierLow,
      digitTarget: details.digitTarget,
      digitLow: details.digitLow,
      digitHigh: details.digitHigh,
      expiryTimeEpoch: details.expiryTimeEpoch,
    };

    let accepted = false;
    let trade: TradeMarker | undefined;
    if (
      details.contractId != null ||
      details.entryPrice != null ||
      details.entryTimeEpoch != null ||
      details.barrier != null ||
      details.barrierHigh != null ||
      details.barrierLow != null ||
      details.digitTarget != null ||
      details.digitLow != null ||
      details.digitHigh != null ||
      details.expiryTimeEpoch != null
    ) {
      trade = applyContractLifecycleEvent(state, {
        type: "order_buy",
        contract_id: details.contractId,
        contract_type: contractType,
        duration,
        duration_unit: durationUnit,
        entry_price: details.entryPrice,
        entry_time_epoch: details.entryTimeEpoch,
        barrier: details.barrier,
        barrier_high: details.barrierHigh,
        barrier_low: details.barrierLow,
        digit_target: details.digitTarget,
        digit_low: details.digitLow,
        digit_high: details.digitHigh,
        expiry_time_epoch: details.expiryTimeEpoch,
      });
      accepted = Boolean(trade);
    } else {
      accepted = addTradeMarker(state, direction, duration, durationUnit);
      trade = state.trades[state.trades.length - 1];
    }

    const finalPayload: TradeExecutionPayload = {
      ...draft,
      ...payload,
      accepted,
      contractId: trade?.contractId ?? payload.contractId,
      entryPrice: trade?.entryPrice ?? payload.entryPrice,
      entryTimeEpoch: trade?.entryTime ?? payload.entryTimeEpoch,
      barrier: trade?.barrier ?? payload.barrier,
      barrierHigh: trade?.barrierHigh ?? payload.barrierHigh,
      barrierLow: trade?.barrierLow ?? payload.barrierLow,
      digitTarget: trade?.digitTarget ?? payload.digitTarget,
      digitLow: trade?.digitLow ?? payload.digitLow,
      digitHigh: trade?.digitHigh ?? payload.digitHigh,
      expiryTimeEpoch: trade?.expiryTime ?? payload.expiryTimeEpoch,
    };
    onExecute?.(finalPayload);
    if (!accepted) return false;
    if (autoFollowLatest) goToLatest(state, 0.3);
    redraw();
    return true;
  }

  return {
    getDraft: () => ({ ...draft }),
    setContractType(contractType: string) {
      draft.contractType = contractType.trim() || defaultContractType;
    },
    setDuration(duration: number, durationUnit?: TradeDurationUnit) {
      draft.duration = Math.max(1, Math.floor(duration || draft.duration));
      if (durationUnit) draft.durationUnit = durationUnit;
    },
    setDurationSeconds(durationSeconds: number) {
      draft.duration = Math.max(1, Math.floor(durationSeconds || defaultDurationSeconds));
      draft.durationUnit = "s";
    },
    setDurationTicks(durationTicks: number) {
      draft.duration = Math.max(1, Math.floor(durationTicks || defaultDurationSeconds));
      draft.durationUnit = "ticks";
    },
    setStake(stake: number) {
      draft.stake = Math.max(1, Math.floor(stake || defaultStake));
    },
    applyLifecycleEvent(event: ContractLifecycleMessage | ContractLifecycleEnvelope) {
      const trade = applyContractLifecycleEvent(state, event);
      if (trade && autoFollowLatest) goToLatest(state, 0.3);
      if (trade) redraw();
      return trade;
    },
    execute,
    executeBuy: (details?: TradeExecutionSubmission) => execute("buy", details),
    executeSell: (details?: TradeExecutionSubmission) => execute("sell", details),
  };
}
